import {useState} from 'react';
import {resolveVariables} from '../lib/template-variables';
import {photoBoxStyle, textBoxMinHeight} from '../lib/element-size';
import {CELL_IMAGE_DEFAULT_HEIGHT, type CellElement} from '../lib/table-types';
import {cellElementToRequirement, cellSignatureConfig, isRequiredCell} from '../lib/cell-fill';
import {cellMarkRoom, RequiredMarkFrame} from './SlotMarks';
import {CheckboxRenderer} from './renderers/choice';
import {ReportImage} from './ReportImage';
import {RichTextEditor, type RichTextEditorHandle} from './RichTextEditor';
import {RichTextView} from './RichTextView';
import {labelText} from '../lib/label-text';
import type {RichText, TextFormat} from '../lib/rich-text';

// ─── CellLogoBadge ───────────────────────────────────────────────────────────

/**
 * The top-left logo badge on an image input inside a table cell. A preview, and
 * only a preview.
 *
 * It used to be a button that opened the logo library, which made a click on the
 * badge mean something different from a click anywhere else on the same element:
 * one opened a modal, the other selected. In a box a couple of centimetres wide
 * that difference is a few pixels of aim, so selecting an image input opened a
 * dialog often enough to be the thing people noticed about it.
 *
 * Choosing the logo now lives in the toolbar, beside the Logo toggle that
 * decides whether one is stamped at all — see `PhotoElementConfig`. The canvas
 * shows what is set; the toolbar sets it.
 */
function CellLogoBadge({logoUrl}: {logoUrl?: string}) {
  return (
    <div className="absolute top-1 left-1 rounded bg-white/85 px-1 shadow-sm">
      {logoUrl ? (
        <ReportImage src={logoUrl} alt="" className="max-h-4 max-w-[44px] object-contain" />
      ) : (
        <span className="text-[8px] font-semibold text-gray-600">LOGO</span>
      )}
    </div>
  );
}

// ─── CellElementView ─────────────────────────────────────────────────────────

interface CellViewProps {
  element: CellElement;
  overrideH?: number;
  overrideW?: number;
  sampleData?: Record<string, string>;
  /** The cell-level text style (font size / bold / colour / align), applied to every
   *  bit of text the element contributes to the cell: a heading's "Text label"
   *  (DERP-244) and a checkbox's / dropdown's option rows (DERP-226).
   *
   *  The remaining types render an input placeholder only. They no longer print
   *  `label` as a small meta-line above it either (DERP-226): a cell's text is the
   *  cell's own content, and a stacked grey caption fought the cell's own styling. */
  textStyle?: React.CSSProperties;
}

/**
 * One cell element as the editor draws it: a static stand-in for the control a
 * crew will fill, plus the required marker the author chose.
 *
 * A required cell must say so HERE too, not only on the fill canvas. The author
 * decides `required` in this editor, and a template that shows no marker while
 * designing but one while filling teaches the author the wrong thing about their
 * own document. The body below draws no marker of its own — unlike the fill
 * renderers, which each draw theirs — so it takes the frame that draws one.
 *
 * A checkbox is the exception: it is drawn by the fill renderer itself (see
 * `CellCheckbox`), so it brings its own marks and must not be framed twice.
 */
export function CellElementView(props: CellViewProps) {
  const {element} = props;
  if (element.type === 'checkbox') {
    return <CellCheckbox element={element} fillMode={false} textStyle={props.textStyle} />;
  }
  return (
    <RequiredMarkFrame show={isRequiredCell(props.element)} config={props.element.config}>
      <CellElementBody {...props} />
    </RequiredMarkFrame>
  );
}

/**
 * A checkbox in a cell, drawn by `CheckboxRenderer` like every other checkbox.
 *
 * The header-row view and the double-click view each drew their own boxes,
 * which is how a setting added to one checkbox had to be added again here. The
 * renderer draws its own marks, so this reserves their room instead of framing
 * them — as `CellElementFill` does for the cells a crew fills.
 */
function CellCheckbox({
  element,
  fillMode,
  textStyle,
}: {
  element: Extract<CellElement, {type: 'checkbox'}>;
  /** Off for the static view; on for the double-click view, where the boxes tick
   *  but nothing is saved. */
  fillMode: boolean;
  textStyle?: React.CSSProperties;
}) {
  const room = cellMarkRoom(element);
  return (
    <div className={room || undefined}>
      <CheckboxRenderer
        label=""
        fillMode={fillMode}
        item={cellElementToRequirement(element, '')}
        textStyle={textStyle}
      />
    </div>
  );
}

/** The per-type render, and nothing else. Split out so the required marker is a
 *  wrapper rather than a condition threaded through every arm. */
function CellElementBody({element, overrideH, overrideW, sampleData, textStyle}: CellViewProps) {
  const ctx = sampleData ?? {};
  const lbl = resolveVariables(element.label, ctx);
  switch (element.type) {
    case 'heading':
      // Normal weight by default — bold is the author's call, via the cell's Bold
      // toggle (or the header-row default), arriving through textStyle like every
      // other cell-text style.
      return (
        <div className="py-0.5">
          <p className="text-sm leading-snug" style={textStyle}>
            {lbl ? (
              <RichTextView value={labelText(element)} sampleData={ctx} />
            ) : (
              <span className="text-gray-300 text-xs">Text label</span>
            )}
          </p>
        </div>
      );
    case 'text': {
      const cfg = element.config;
      const multiline = !cfg.input_type || cfg.input_type === 'textarea';
      return (
        <div className="space-y-0.5">
          <div
            className="w-full border border-gray-200 rounded bg-gray-50 px-1.5 py-1 text-xs text-gray-400 italic"
            // The floor the REPORT draws — see `textBoxMinHeight`. This arm
            // used to carry its own `h-6`/`min-h-[32px]`, which is how the box a
            // JAC was designed against came to be shorter than the one that
            // printed, and how its tables came to overflow what sat below them.
            style={{minHeight: textBoxMinHeight({singleLine: !multiline})}}
          >
            {resolveVariables(cfg.placeholder ?? '', ctx)}
          </div>
        </div>
      );
    }
    case 'photo': {
      const cfg = element.config;
      const h = overrideH ?? cfg.height ?? CELL_IMAGE_DEFAULT_HEIGHT;
      const w = overrideW ?? cfg.width;
      // No exception for a drag in progress. A locked box offers no resize
      // corner at all (see `canResize` in `TableElement`), so `overrideH` and
      // `square` cannot both be set -- and the exception that used to live here
      // was what let the box follow the pointer and then snap back to the ratio
      // on release.
      const square = cfg.square === true;
      return (
        <div className="space-y-0.5">
          <div
            className="relative border border-gray-200 rounded bg-gray-50 flex items-center justify-center text-gray-300 text-[10px] overflow-hidden"
            style={photoBoxStyle({width: w ?? '100%', height: h, square, heightIsMinimum: true})}
          >
            Image input
            {cfg.show_logo && (
              /* Preview the chosen logo rather than the word LOGO, matching the
                 standalone slot's badge — an author needs to see WHICH logo will
                 be burned in, not just that one will be. A preview on both
                 surfaces and a picker on neither: choosing is the toolbar's, in
                 `PhotoOverlayControls`. */
              <CellLogoBadge logoUrl={cfg.logo_url} />
            )}
            {cfg.show_map && (
              <div className="absolute bottom-1 right-1 flex h-5 w-6 items-center justify-center rounded border border-white/70 bg-gradient-to-br from-emerald-100 to-sky-100 text-[8px] text-rose-500">
                ◍
              </div>
            )}
            {(cfg.show_datetime || cfg.show_latlng) && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                {cfg.show_datetime && (
                  <p className="text-[9px] text-white font-mono">Date / Time</p>
                )}
                {cfg.show_latlng && <p className="text-[9px] text-white font-mono">GPS Location</p>}
              </div>
            )}
            {cfg.show_compass && (
              <div className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/50 flex items-center justify-center text-white text-[9px] font-bold">
                N
              </div>
            )}
          </div>
        </div>
      );
    }
    case 'logo': {
      const cfg = element.config;
      const w = overrideW ?? cfg.width ?? 80;
      const h = overrideH ?? cfg.height ?? 40;
      const justifyMap: Record<string, string> = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
      };
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: justifyMap[cfg.align ?? 'left'] ?? 'flex-start',
          }}
        >
          {cfg.logo_url ? (
            <ReportImage
              src={cfg.logo_url}
              alt="Logo"
              style={{width: w, height: h, objectFit: 'contain', maxWidth: '100%'}}
            />
          ) : (
            <div
              className="flex items-center justify-center border border-dashed border-gray-300 bg-gray-50 rounded text-gray-300 text-[10px]"
              style={{width: w, height: h}}
            >
              Photo
            </div>
          )}
        </div>
      );
    }
    case 'dropdown': {
      const opts = element.config.options ?? [];
      return (
        <div className="space-y-0.5">
          <div
            className="flex items-center justify-between border border-gray-200 rounded bg-gray-50 px-2 py-0.5 text-xs text-gray-400"
            style={textStyle}
          >
            <span>{opts[0] ?? 'Select…'}</span>
            <span>▾</span>
          </div>
        </div>
      );
    }
    case 'signature': {
      // Through the same resolver the fill path uses, so the two lines beneath
      // the pad are drawn — or not — by one decision. Read raw, this arm saw
      // `undefined` for both flags on every signature `blankCellElement`
      // authored, drew neither line, and left the author laying a JAC out
      // against a cell shorter than the one that prints.
      const cfg = cellSignatureConfig(element.config);
      return (
        <div className="space-y-0.5">
          <div
            className="border border-gray-300 rounded bg-gray-50 flex items-center justify-center text-gray-300 text-[10px]"
            style={{width: cfg.width, minHeight: cfg.height}}
          >
            {cfg.placeholder || 'Signature'}
          </div>
          {(cfg.show_signer_name || cfg.show_signed_at) && (
            <div className="mt-0.5 space-y-0.5" style={{width: cfg.width}}>
              {cfg.show_signer_name && (
                <div className="border-b border-dashed border-gray-300 text-[9px] text-gray-400">
                  Name
                </div>
              )}
              {cfg.show_signed_at && (
                <div className="border-b border-dashed border-gray-300 text-[9px] text-gray-400">
                  Signed at
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}

// ─── CellElementInteractive ──────────────────────────────────────────────────
// Renders each element type in a fully interactive state (used when the cell is
// in "edit mode"). Ephemeral interaction state (ticked boxes, the dropdown
// selection) is intentionally NOT persisted — this is a design-time preview.
// Only heading elements persist their change back via onDraftChange.

export function CellElementInteractive({
  element,
  draft,
  onDraftChange,
  onFinish,
  onCancel,
  onTab,
  textStyle,
  editorRef,
  owner,
  base,
}: {
  element: CellElement;
  draft: RichText;
  onDraftChange: (v: RichText) => void;
  onFinish: (restoreFocus?: boolean) => void;
  onCancel: () => void;
  onTab: (e: React.KeyboardEvent) => void;
  /** Cell-level text style — applied to the heading editor and the option rows so
   *  text is the same size while being typed as once committed. As in
   *  CellElementView, no element prints its `label` as a meta-line (DERP-226). */
  textStyle?: React.CSSProperties;
  /** The label editor, for the caller to place the caret on mount.
   *  Every case that edits `draft` as text must forward it. */
  editorRef?: React.Ref<RichTextEditorHandle>;
  /** The table's id: whose toolbar formats the selected words (DERP-307). */
  owner: string;
  /** What the label's words inherit from the cell. */
  base: TextFormat;
}) {
  const opts = 'options' in element.config ? (element.config.options ?? []) : [];
  const [dropdownValue, setDropdownValue] = useState(() => opts[0] ?? '');

  const sharedKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      onTab(e);
      return;
    }
    e.stopPropagation();
  };

  // Fires when focus leaves the interactive container entirely.
  const containerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      onFinish();
    }
  };

  switch (element.type) {
    case 'heading':
      return (
        <RichTextEditor
          // Forwarded so the caret opens where the author clicked.
          ref={editorRef}
          value={draft}
          onChange={onDraftChange}
          owner={owner}
          base={base}
          style={textStyle}
          className="w-full text-sm leading-snug bg-transparent block"
          onBlurAway={() => onFinish()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onFinish(true);
              return;
            }
            sharedKeyDown(e);
          }}
        />
      );

    case 'text': {
      const cfg = element.config;
      const multiline = !cfg.input_type || cfg.input_type === 'textarea';
      return (
        <div className="space-y-0.5" onBlur={containerBlur}>
          {multiline ? (
            <textarea
              autoFocus
              placeholder={cfg.placeholder ?? 'Enter text…'}
              className="w-full border border-gray-300 rounded bg-white px-1.5 py-1 text-xs min-h-[32px] outline-none focus:border-indigo-400 resize-none overflow-hidden"
              onKeyDown={sharedKeyDown}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          ) : (
            <input
              autoFocus
              type="text"
              placeholder={cfg.placeholder ?? 'Enter text…'}
              className="w-full border border-gray-300 rounded bg-white px-1.5 py-1 text-xs h-6 outline-none focus:border-indigo-400"
              onKeyDown={sharedKeyDown}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      );
    }

    case 'checkbox':
      return (
        <div onBlur={containerBlur} onKeyDown={sharedKeyDown} onClick={(e) => e.stopPropagation()}>
          <CellCheckbox element={element} fillMode textStyle={textStyle} />
        </div>
      );

    case 'dropdown':
      return (
        <div className="space-y-0.5" onBlur={containerBlur}>
          <select
            autoFocus
            value={dropdownValue}
            onChange={(e) => setDropdownValue(e.target.value)}
            style={textStyle}
            className="w-full border border-gray-300 rounded bg-white px-1.5 py-0.5 text-xs outline-none focus:border-indigo-400 cursor-pointer"
            onKeyDown={sharedKeyDown}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {opts.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );

    // An image input, a photo and a signature have nothing to type into: the
    // crew fills them, the author only places them. These arms used to draw a
    // dashed indigo variant of the ordinary view, so selecting one made the
    // element visibly morph — a different border, a different fill, a different
    // size — to announce a mode that offers nothing. The selection ring the
    // wrapper already draws is the whole of what "this one is selected" needs to
    // say, so they render exactly what they render unselected.
    case 'photo':
    case 'logo':
    case 'signature':
      return <CellElementView element={element} textStyle={textStyle} />;

    default:
      return null;
  }
}
